// Direction B "Ink Header" panel.
// Per spec sec 1.10 + Direction B handoff:
//   - Mobile (<640px): fullscreen modal — w-full h-full, z-index 2147483646.
//   - Desktop (>=640px): 380×640 popover anchored bottom-right (16px gap),
//     16px radius, panel shadow.
//   - Header: warm-ink (#1a1614) bg, 1px (#2a2421) bottom border, avatar +
//     title + pink-dot status row + close X.
//
// Layout (top→bottom): Header → children (thread/welcome/etc.) → footer.
// The footer is rendered by App.tsx via the children slot to keep the Powered
// By line below the composer.

import { ReactNode } from 'react'
import { CLOSE_SVG } from '../icons/icons'
import { MiklaAvatar } from './MiklaAvatar'

export interface ChatPanelProps {
  onClose: () => void
  children: ReactNode
  // Venue title shown in the warm-ink header. Defaults to a neutral string
  // for the brief loading window before the probe response lands; once
  // available the App passes vendor_info.business_name.
  title?: string
  // When true, render the "● HOW CAN I HELP?" status row under the title.
  // Default true; only the bare-error fallback turns it off because the
  // surface state isn't a normal conversation when the session failed to load.
  showStatus?: boolean
}

export function ChatPanel({
  onClose,
  children,
  title = 'Mikla Estate',
  showStatus = true,
}: ChatPanelProps) {
  return (
    <div
      data-testid="chat-panel"
      className={[
        // Positioning
        'fixed inset-0 sm:inset-auto sm:bottom-4 sm:right-4',
        // Sizing
        'w-full h-full sm:w-[380px] sm:h-[640px] sm:max-h-[calc(100vh-32px)]',
        // Surface
        'bg-paper sm:rounded-2xl sm:border border-border-muted sm:shadow-panel',
        'flex flex-col overflow-hidden',
        // Open animation (240ms ease-out per spec)
        'animate-panelOpen',
      ].join(' ')}
      style={{ zIndex: 2147483646 }}
    >
      {/* ── Warm-ink header ─────────────────────────────────────────── */}
      <div
        data-testid="chat-header"
        className="flex-shrink-0 flex items-center justify-between gap-3 bg-warmink border-b border-warmink-border px-4 sm:pl-[18px] sm:pr-4 py-4"
      >
        <div className="flex items-center gap-[11px] min-w-0">
          <MiklaAvatar size="md" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white leading-tight tracking-[-0.005em] truncate">
              {title}
            </div>
            {showStatus && (
              <div className="font-mono text-[10px] font-medium uppercase tracking-eyebrow-tight text-muted-400 inline-flex items-center gap-1.5 mt-[3px]">
                <span
                  className="w-[5px] h-[5px] rounded-full bg-accent flex-shrink-0"
                  style={{ boxShadow: '0 0 0 3px rgba(255,0,65,0.18)' }}
                />
                How can I help?
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          data-testid="chat-close"
          onClick={onClose}
          aria-label="Close chat"
          className="w-7 h-7 rounded-md flex items-center justify-center text-muted-400 hover:bg-white/10 hover:text-white transition-colors flex-shrink-0"
        >
          <span
            dangerouslySetInnerHTML={{ __html: CLOSE_SVG }}
            className="w-3.5 h-3.5 inline-block"
          />
        </button>
      </div>

      {/* ── Body slot (thread / welcome / loading / error / footer) ── */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">{children}</div>
    </div>
  )
}
